import React, {useEffect, useState} from "react";
import {Form} from "react-bootstrap";
import cogoToast from "cogo-toast";
import {landlordService} from "../../services/landlordService";
import HybridIntegrationReport from "./HybridIntegrationReport";

const integrationTypes = [
    {
        value: "vivin",
        label: "Vivin Integration",
        description: "Keeps the landlord's Inlife listings and syncs availability, rents, stay periods and fees from Vivin."
    },
    {
        value: "hybrid",
        label: "Full Vivin Integration",
        description: "Listings are read from the integration URL and only mirrored on Inlife. The landlord's regular listings are disabled."
    }
];

const LandlordIntegrationForm = ({landlord, landlordId, errorMessages = {}, onChange}) => {
    const [type, setType] = useState(landlord.vivin_integration ? "hybrid" : "vivin");
    const [integrationUrl, setIntegrationUrl] = useState("");
    const [confirmDisable, setConfirmDisable] = useState(false);
    // primeira passagem: o feed e validado antes de ativar, e os listings que falham so ficam de fora com confirmacao
    const [report, setReport] = useState(null);
    const [checking, setChecking] = useState(false);
    const [confirmSkipInvalid, setConfirmSkipInvalid] = useState(false);

    // o db_landlord conta as propriedades nao eliminadas, incluindo as ja desativadas
    const propertiesCount = landlord.integration_landlord ? 0 : (landlord.houses || 0);

    useEffect(() => {
        onChange({type, integrationUrl: integrationUrl.trim(), confirmDisable, propertiesCount, report, confirmSkipInvalid});
    }, [type, integrationUrl, confirmDisable, report, confirmSkipInvalid]);

    const changeIntegrationUrl = (value) => {
        // o relatorio deixa de corresponder ao URL
        setIntegrationUrl(value);
        setReport(null);
        setConfirmSkipInvalid(false);
    };

    const checkFeed = async () => {
        if (!integrationUrl.trim() || checking) return;
        setChecking(true);
        setReport(null);
        setConfirmSkipInvalid(false);
        try {
            setReport(await landlordService.checkHybridIntegration(landlordId, integrationUrl.trim()));
        } catch (e) {
            let message = e.error?.message || e.validationErrors?.[0]?.msg || e.message;
            cogoToast.error(errorMessages[message] || message || "Something went wrong", {hideAfter: 6, position: 'top-center', heading: 'Error'});
        }
        setChecking(false);
    };

    return (
        <form className="forms-sample">
            <Form.Group>
                {integrationTypes.map(integrationType => (
                    <div className="form-check" key={integrationType.value}>
                        <label className="form-check-label">
                            <input type="radio" className="form-check-input" name="integrationType"
                                   value={integrationType.value} checked={type === integrationType.value}
                                   disabled={integrationType.value === "vivin" && landlord.vivin_integration}
                                   onChange={() => setType(integrationType.value)}/>
                            <i className="input-helper"/>
                            {integrationType.label}
                            {integrationType.value === "vivin" && landlord.vivin_integration ? " (already active)" : ""}
                        </label>
                        <small className="d-block text-muted">{integrationType.description}</small>
                    </div>
                ))}
            </Form.Group>
            {type === "hybrid" && <>
                <Form.Group>
                    <label htmlFor="integrationUrl">Integration URL</label>
                    <div className="d-flex">
                        <Form.Control type="text" className="form-control" id="integrationUrl" name="integrationUrl"
                                      value={integrationUrl} placeholder="https://..."
                                      onChange={(e) => changeIntegrationUrl(e.target.value)}/>
                        <button type="button" className="btn btn-outline-primary btn-sm ml-2 text-nowrap"
                                disabled={!integrationUrl.trim() || checking} onClick={checkFeed}>
                            {checking ? "Checking..." : "Check feed"}
                        </button>
                    </div>
                    {!report && <small className="d-block text-muted mt-1">
                        Check the feed before activating. Nothing is published or disabled by the check.
                    </small>}
                </Form.Group>
                {report && <Form.Group>
                    <HybridIntegrationReport report={report}/>
                    {report.valid === 0 ? <p style={{color: "#f3454e"}}>
                        No listing passes the required checks, so the integration can't be activated yet.
                        Send the failing listings to the provider.
                    </p> : report.invalid > 0 && <div className="form-check">
                        <label className="form-check-label">
                            <input type="checkbox" className="form-check-input" id="confirmSkipInvalid"
                                   checked={confirmSkipInvalid} onChange={(e) => setConfirmSkipInvalid(e.target.checked)}/>
                            <i className="input-helper"/>
                            I understand that {report.invalid} of {report.total} listings won't be published until the provider fixes them
                        </label>
                    </div>}
                </Form.Group>}
                {propertiesCount > 0 && <Form.Group>
                    <p style={{color: "#f3454e"}}>
                        This landlord has {propertiesCount} {propertiesCount === 1 ? "property" : "properties"} on Inlife.
                        All of them will be <strong>disabled</strong> and removed from search.
                    </p>
                    <div className="form-check">
                        <label className="form-check-label">
                            <input type="checkbox" className="form-check-input" id="confirmDisable"
                                   checked={confirmDisable} onChange={(e) => setConfirmDisable(e.target.checked)}/>
                            <i className="input-helper"/>
                            I confirm that all of this landlord's regular properties will be disabled
                        </label>
                    </div>
                </Form.Group>}
            </>}
        </form>
    )
}

export default LandlordIntegrationForm;
